import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useActions } from "../../utils/reactQueryHooks";
import { useCardData } from "../common/CardContext";
import Loading from "../common/Loading";
import ChatBot from "../common/ChatBot";
import chatIcon from "../../assets/images/chat.png";
import { cameraMock } from './mockData';
import { loadPlantsFromStorage } from '../../utils/plantStorageUtils';

// Import camera components
import HeaderBar from './HeaderBar';
import SnapshotViewer from './SnapshotViewer';
import TimelineSlider from './TimelineSlider';
import PlantDetailsPanel from './PlantDetailsPanel';
import SensorOverlay from './SensorOverlay';
import PlantZoomView from './PlantZoomView';
import { useChatUIPersistence } from "../common/ChatUIPersistenceContext";

import "../../styles/chatbot.css";
import "../../styles/camera.css";

export default function CameraWrapper() {
    const { macAddress } = useParams();
    const location = useLocation();
    
    // Chatbot state
    const { isChatModalOpen, setIsChatModalOpen } = useChatUIPersistence();
    
    // Camera-specific state
    const [selectedTimeRange, setSelectedTimeRange] = useState("24h");
    const [selectedPlant, setSelectedPlant] = useState(null);
    const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
    const [currentSnapshot, setCurrentSnapshot] = useState(null);
    const [snapshotHistory, setSnapshotHistory] = useState([]);
    const [plants, setPlants] = useState([]);
    const [cameraData, setCameraData] = useState(null);
    
    // Ref for the snapshot image element
    const snapshotImageRef = useRef(null);

    // Handle plants update from SnapshotViewer
    const handlePlantsUpdate = (updatedPlants) => {
        setPlants(updatedPlants);
    };

    // Initialize plants from localStorage when component mounts
    useEffect(() => {
        const storedPlants = loadPlantsFromStorage('default');
        if (storedPlants.length > 0) {
            setPlants(storedPlants);
        } else {
            // Use mock data if no stored plants
            setPlants(cameraMock.plants || []);
        }
    }, []);
    
    // ChatBot functions
    const openChatBot = () => setIsChatModalOpen(prev => !prev);
    const closeChatBot = () => setIsChatModalOpen(false);

    const locationState = location.state || {};
    const locationAction = locationState.action || {};

    // Use React Query to fetch/refresh actions data
    const { data: actions = [], isLoading: isActionsLoading } = useActions();
    const actionCard = useMemo(() => 
        actions.find(action => action.macAddress === macAddress),
    [actions, macAddress]
    );

    // Get real-time MQTT data from context
    const { cardData } = useCardData();
    const latestData = cardData[actionCard?.sensor] ?? null;
    const currentTimestamp = latestData?.mostRecentTimestamp ?? null;

    // Initialize mock data
    useEffect(() => {
        setCameraData(cameraMock);
        setCurrentSnapshot(cameraMock.snapshots[0]);
        setSnapshotHistory(cameraMock.snapshots);
        setPlants(cameraMock.plants);
    }, []);

    // Handle plant selection
    const handlePlantSelect = (plantId) => {
        setSelectedPlant(plantId);
    };

    // Handle snapshot capture (mock)
    const handleCaptureSnapshot = async () => {
        setIsSnapshotLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const newSnapshot = {
            ...cameraMock.snapshots[0],
            id: `snapshot_${Date.now()}`,
            timestamp: new Date().toISOString(),
            sensorData: {
                temperature: latestData?.mostRecentTemp || 72,
                humidity: latestData?.mostRecentHumidity || 65,
                light: latestData?.mostRecentLight || 450,
                water: latestData?.mostRecentWater || 85
            }
        };
        
        setCurrentSnapshot(newSnapshot);
        setSnapshotHistory(prev => [newSnapshot, ...prev.slice(0, 9)]); // Keep last 10
        setIsSnapshotLoading(false);
    };

    // Handle time range change
    const handleTimeRangeChange = (range) => {
        setSelectedTimeRange(range);
        // In real implementation, this would fetch historical snapshots
    };

    // Handle snapshot selection
    const handleSnapshotSelect = (snapshot) => {
        setCurrentSnapshot(snapshot);
    };

    // Determine if we should show loading state
    const isLoading = useMemo(() => {
        return (isActionsLoading && !actionCard && !locationAction?.macAddress) || 
               (isActionsLoading && (!actionCard || Object.keys(actionCard).length === 0));
    }, [isActionsLoading, actionCard, locationAction]);

    // If we're loading, show the loading component
    if (isLoading) {
        return <Loading message="Loading camera data..." />;
    }

    return (
        <div className="camera-wrapper">
            {/* Header */}
            <HeaderBar 
                cameraData={cameraData}
                onCaptureSnapshot={handleCaptureSnapshot}
                isCapturing={isSnapshotLoading}
            />

            {/* Sensor Data Row */}
            <SensorOverlay 
                sensorData={currentSnapshot?.sensorData}
                currentTimestamp={currentTimestamp}
            />

            {/* Main Camera Interface */}
            <div className="camera-main-content">
                {/* Left Column - Snapshot Viewer */}
                <div className="camera-left-column">
                    <SnapshotViewer 
                        currentSnapshot={currentSnapshot}
                        plants={plants}
                        selectedPlant={selectedPlant}
                        onPlantSelect={handlePlantSelect}
                        onPlantsUpdate={handlePlantsUpdate}
                        imageRef={snapshotImageRef}
                    />
                    <PlantZoomView 
                        selectedPlant={selectedPlant}
                        plants={plants}
                        currentSnapshot={currentSnapshot}
                        snapshotImageRef={snapshotImageRef}
                    />
                </div>

                {/* Right Column - Timeline and Plant Details */}
                <div className="camera-right-column">
                    <TimelineSlider 
                        snapshots={snapshotHistory}
                        currentSnapshot={currentSnapshot}
                        onSnapshotSelect={handleSnapshotSelect}
                        selectedTimeRange={selectedTimeRange}
                        onTimeRangeChange={handleTimeRangeChange}
                        timeRanges={cameraMock.timeRanges}
                    />
                    <PlantDetailsPanel 
                        selectedPlant={selectedPlant}
                        plants={plants}
                    />
                </div>
            </div>

            {/* ChatBot Button */}
            <button 
                className="chatbot-toggle-button" 
                onClick={openChatBot}
                aria-label="Open Chat Assistant"
                title="Chat with Canopy AI Assistant"
            >
                <img src={chatIcon} alt="Chat" className="chatbot-icon" />
            </button>
            
            {/* ChatBot Modal */}
            {isChatModalOpen && (
                <ChatBot 
                    isModal={true}
                    closeChatModal={closeChatBot}
                    modalPosition="bottom-right"
                    metaData={{
                        macAddress: macAddress,
                        actionType: 'camera',
                        timeRange: selectedTimeRange
                    }}
                />
            )}
        </div>
    );
} 